/**
 * curriculum-api.ts
 * Client for the TeachBud Curriculum Hub API (artifacts/api-server).
 * The API server is mounted at /api in the proxy, so all requests go to /api/curriculum/...
 */

const API_BASE = '/api/curriculum';

// ── Types (inlined from @workspace/api-zod to avoid cross-package tsconfig refs) ──

export interface CurriculumExtract {
  title: string;
  subject: string;
  grade: string;
  standards: string[];
  objective: string;
  vocabulary: string[];
  instructionalSequence: string;
  activities: string;
  assessments: string;
  publisher: string;
  unitInfo: string;
  notes: string;
}

export interface CurriculumSummary {
  id: number;
  title: string;
  subject: string;
  gradeLevel: string;
  publisher: string;
  scope: string;
  sourceType: string;
  fileName: string | null;
  createdAt: Date;
}

export interface UploadResult {
  id: number;
  preview: string;
  charCount: number;
}

export interface ExtractResult {
  id: number;
  extract: CurriculumExtract;
}

export interface CurriculumDetail {
  id: number;
  ownerId: string;
  scope: string;
  title: string;
  subject: string;
  gradeLevel: string;
  publisher: string;
  sourceType: string;
  sourceUrl: string | null;
  fileType: string | null;
  fileName: string | null;
  rawText: string;
  extractedData: CurriculumExtract | null;
  createdAt: string;
  updatedAt: string;
}

// ── API helpers ───────────────────────────────────────────────────────────────

function authHeaders(userId: string): HeadersInit {
  return { 'X-User-Id': userId };
}

async function handleResponse<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    let msg = `Request failed (${resp.status})`;
    try {
      const body = await resp.json();
      if (typeof body?.error === 'string') msg = body.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return resp.json() as Promise<T>;
}

// ── Upload a file ─────────────────────────────────────────────────────────────

export async function uploadCurriculumFile(
  file: File,
  userId: string,
): Promise<UploadResult> {
  const form = new FormData();
  form.append('file', file);
  const resp = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    headers: authHeaders(userId),
    body: form,
  });
  return handleResponse<UploadResult>(resp);
}

// ── Import from URL ───────────────────────────────────────────────────────────

export async function uploadCurriculumUrl(
  url: string,
  userId: string,
): Promise<UploadResult> {
  const resp = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    headers: { ...authHeaders(userId), 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  return handleResponse<UploadResult>(resp);
}

// ── AI extraction ─────────────────────────────────────────────────────────────

export async function extractCurriculum(
  id: number,
  userId: string,
  force = false,
): Promise<ExtractResult> {
  const url = force ? `${API_BASE}/${id}/extract?force=true` : `${API_BASE}/${id}/extract`;
  const resp = await fetch(url, { method: 'POST', headers: authHeaders(userId) });
  return handleResponse<ExtractResult>(resp);
}

// ── Rename curriculum ─────────────────────────────────────────────────────────

export async function renameCurriculum(
  id: number,
  title: string,
  userId: string,
): Promise<{ id: number; title: string }> {
  const resp = await fetch(`${API_BASE}/${id}`, {
    method: 'PATCH',
    headers: { ...authHeaders(userId), 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  return handleResponse<{ id: number; title: string }>(resp);
}

// ── List curricula ────────────────────────────────────────────────────────────

export async function listCurricula(
  userId: string,
  scope: string = 'my',
): Promise<CurriculumSummary[]> {
  const resp = await fetch(`${API_BASE}?scope=${encodeURIComponent(scope)}`, {
    headers: authHeaders(userId),
  });
  const data = await handleResponse<{ curricula: CurriculumSummary[] }>(resp);
  return data.curricula;
}

// ── Get single curriculum ─────────────────────────────────────────────────────

export async function getCurriculum(
  id: number,
  userId: string,
): Promise<CurriculumDetail> {
  const resp = await fetch(`${API_BASE}/${id}`, {
    headers: authHeaders(userId),
  });
  const data = await handleResponse<{ curriculum: CurriculumDetail }>(resp);
  return data.curriculum;
}

// ── Delete curriculum ─────────────────────────────────────────────────────────

export async function deleteCurriculum(
  id: number,
  userId: string,
): Promise<void> {
  const resp = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(userId),
  });
  await handleResponse<{ success: boolean }>(resp);
}
